import { Activity, Building2, CalendarDays, Clock, Euro, FileText, Home, KeyRound, LayoutGrid, Lock, Settings2, Users, type LucideIcon } from "lucide-react";
import type { Crm } from "@/hooks/useCrm";
import { boardIcon } from "./boards";
import { ACTIVE_CONTRACT_PHASES, OPEN_TASK, firstId, str } from "./crm";
import { daysUntil } from "./dates";
import type { TableKey } from "./types";

// De hoofdnavigatie (vroeger catalog.js). Elk scherm heeft een eigen adres, zodat je een link
// naar een pand of een scherm kan delen of een bladwijzer kan maken.

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Alleen zichtbaar met dit recht. */
  permission?: string;
  /** Teller naast het menu-item. */
  count?: (crm: Crm) => number;
  /** Alleen actief op precies dit adres (niet op adressen eronder). */
  end?: boolean;
}

export const NAVIGATION: { label: string; items: NavItem[] }[] = [
  {
    label: "Werken",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutGrid },
      { to: "/relaties", label: "Leads & relaties", icon: Users, count: (c) => c.all("contacts").filter((x) => ["Nieuw", "Contact", "Intake", "Follow-up"].includes(str(x, "Status"))).length },
      { to: "/panden", label: "Eigenaars & panden", icon: Building2, count: (c) => c.all("properties").length },
      { to: "/verhuur", label: "Verhuur & matching", icon: Home, count: (c) => c.all("requests").filter((r) => !["Geplaatst", "Verloren"].includes(str(r, "Fase"))).length },
      { to: "/contracten", label: "Contract & intrede", icon: FileText, count: (c) => c.all("contracts").filter((k) => !ACTIVE_CONTRACT_PHASES.includes(str(k, "Fase")) && str(k, "Fase") !== "Afgesloten").length },
      { to: "/actieve-verhuur", label: "Actieve verhuur", icon: Activity, count: (c) => c.all("contracts").filter((k) => ACTIVE_CONTRACT_PHASES.includes(str(k, "Fase"))).length },
      { to: "/uittrede", label: "Verlenging & uittrede", icon: KeyRound, count: (c) => c.all("contracts").filter((k) => ACTIVE_CONTRACT_PHASES.includes(str(k, "Fase")) && (daysUntil(str(k, "Einde")) ?? 999) <= 90).length },
      { to: "/planning", label: "Planning & taken", icon: CalendarDays, count: (c) => c.all("tasks").filter(OPEN_TASK).length },
      { to: "/facturen", label: "Facturen", icon: Euro, permission: "facturen.lezen", count: (c) => c.invoices.filter((i) => i.overdue).length }
    ]
  },
  {
    label: "Beheer",
    items: [
      { to: "/gebruikers", label: "Gebruikers", icon: Users, permission: "gebruikers.beheren" },
      { to: "/auditlog", label: "Auditlog", icon: Clock, permission: "audit.lezen" },
      { to: "/instellingen", label: "Instellingen", icon: Lock, permission: "platform.beheren" }
    ]
  }
];

/**
 * De groep "Borden" (fase 9): elk eigen bord is een tabblad met als teller het aantal kaarten.
 * Wie borden beheert, ziet ook het overzicht (nieuw bord, volgorde, gearchiveerde borden).
 */
export function boardNavigation(crm: Crm): { label: string; items: NavItem[] } | null {
  const items: NavItem[] = crm.boards.map((board) => ({ to: `/borden/${board.id}`, label: board.name, icon: boardIcon(board.icon), count: () => board.cardCount }));
  if (crm.can("borden.beheren")) items.push({ to: "/borden", label: "Borden beheren", icon: Settings2, end: true });
  return items.length ? { label: "Borden", items } : null;
}

/**
 * Het scherm waar een record thuishoort (zoeken, links in lijsten). Een contact opent het pand
 * waarvan het eigenaar of huurder is, anders zijn bedrijf, anders de relatielijst.
 */
export function recordPath(crm: Crm, table: TableKey, id: string): string {
  switch (table) {
    case "properties":
      return `/panden/${id}`;
    case "companies":
      return `/bedrijven/${id}`;
    case "requests":
      return `/verhuur?vraag=${id}`;
    case "contracts":
      return ACTIVE_CONTRACT_PHASES.includes(str(crm.get("contracts", id), "Fase")) ? `/uittrede?contract=${id}` : `/contracten?contract=${id}`;
    case "tickets":
      return `/actieve-verhuur?ticket=${id}`;
    case "contacts": {
      const property = crm.linked("properties", "Eigenaar", id)[0] ?? crm.linked("properties", "Huurder", id)[0];
      if (property) return `/panden/${property.id}`;
      const company = firstId(crm.get("contacts", id), "Bedrijf");
      if (company) return `/bedrijven/${company}`;
      return `/relaties?tab=${str(crm.get("contacts", id), "Rol") === "Investeerder" ? "investeerders" : "leads"}`;
    }
    default:
      return "/planning";
  }
}
